import { useEffect, useRef, useState } from 'react';

/**
 * Returns the previous value of a given state. Useful when you need to know the
 * value a state changed _from_ when handling the change.
 *
 * TODO: Investigate whether React's builtin `useDeferredValue` satisfies the
 * same need and replace usage if it does.
 *
 * @param value The "current" stateful value
 *
 * @example
 * ```tsx
 * const [count, setCount] = useState(0);
 * const previousCount = usePrevious(count);
 *
 * useEffect(() => {
 *   console.log({
 *     count, // 1,2,3...
 *     previousCount, // 0,1,2...
 *   });
 * }, [count]);
 * ```
 */
export const usePrevious = <T>(value: T) => {
  // Initialize a ref with the current value
  const previous = useRef<T>(value);
  const [current, setCurrent] = useState(() => value);

  // When the value changes, update the ref on the next cycle
  useEffect(() => {
    if (value === current) return;

    setCurrent(previousCurrent => {
      previous.current = previousCurrent;
      return value;
    });
  }, [value]);

  // Return the ref value. If the state has just changed, this will be the
  // previous value until the next cycle.
  return previous.current;
};

export default usePrevious;
